import AdditionalBadgesJewelV3 from 'Components/AdditionalBadgesJewel/AdditionalBadgesJewelV3';
import BulletDividerV3 from 'Components/BulletDivider/BulletDividerV3';
import React from 'react';
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Text,
  Link,
  Badge,
  CardProps as CardPropsType,
} from 'pounceV2';
import { Permission, DetectionType } from 'Generated/schema';
import { Link as RRLink } from 'react-router-dom';
import urls from 'Source/urls';
import SeverityBadgeV3 from 'Components/badges/SeverityBadge/SeverityBadgeV3';
import StatusBadgeV3 from 'Components/badges/StatusBadge/StatusBadgeV3';
import { LinkButtonV3 } from 'Components/buttons/LinkButton';
import PageHeaderV3 from 'Components/PageHeader/PageHeaderV3';
import BulletedValueV3 from 'Components/BulletedValue/BulletedValueV3';
import RelatedDestinationsV3 from 'Components/RelatedDestinations/RelatedDestinationsV3';
import useDetectionDestinations from 'Hooks/useDetectionDestinations';
import RoleRestrictedAccess from 'Components/utils/RoleRestrictedAccess';
import LimitItemDisplayV3 from 'Components/LimitItemDisplay/LimitItemDisplayV3';
import ManagedBadgeV3 from 'Components/badges/ManagedBadge/ManagedBadgeV3';
import { RuleDetails } from 'Source/graphql/fragments/RuleDetails.generated';
import useRelationRule from 'Hooks/useRelationRule';
import useUrlParams from 'Hooks/useUrlParams';
import { useTheme } from '@mui/material';

interface ResourceDetailsInfoProps {
  rule?: RuleDetails;
  CardProps?: CardPropsType;
  omitControls?: boolean;
  omitBadges?: boolean;
  linkToRule?: boolean | Record<string, any>;
  linkExternal?: boolean;
}

const RuleDetailsBannerV3: React.FC<ResourceDetailsInfoProps> = ({
  rule,
  CardProps = {},
  omitControls = false,
  omitBadges = false,
  linkToRule = false,
  linkExternal = false,
}) => {
  const {
    palette: { tokens, typographyTokens },
  } = useTheme();
  const { detectionDestinations, loading: loadingDetectionDestinations } = useDetectionDestinations(
    { detection: rule },
  );
  const { relationRule } = useRelationRule();
  const { stringifyUrlParams } = useUrlParams();

  const isScheduledRule = rule.analysisType === DetectionType.ScheduledRule;
  const ruleTypeAccentColor =
    rule.analysisType === DetectionType.ScheduledRule ||
    rule.analysisType === DetectionType.CorrelationRule
      ? tokens.content.assets.semantic.suggest.base
      : tokens.content.assets.semantic.info.base;

  return (
    <React.Fragment>
      {!omitControls && (
        <RoleRestrictedAccess allowedPermissions={[Permission.RuleModify]}>
          <PageHeaderV3.Actions>
            <Flex gap={4} justify="flex-end">
              <LinkButtonV3
                variant="outline"
                data-tid="edit-rule"
                aria-label="Edit Rule"
                to={urls.build.detections.rules.edit(rule.id)}
              >
                Edit
              </LinkButtonV3>
              {/* Hide delete button for managed detections to prevent orphaned clones */}
              {rule.managed && (
                <Button
                  onClick={() => relationRule(rule, 'clone')}
                  variant="outline"
                  data-tid="clone-rule"
                >
                  Clone & edit
                </Button>
              )}
            </Flex>
          </PageHeaderV3.Actions>
        </RoleRestrictedAccess>
      )}
      <Card
        as="article"
        p={8}
        variant="midToBrand"
        borderLeft="2px solid"
        borderLeftColor={ruleTypeAccentColor}
        display="flex"
        flexDirection="column"
        gap={1}
        {...CardProps}
      >
        <Flex as="header" align="center">
          {linkToRule ? (
            <Link
              aria-describedby="rule-description"
              as={RRLink}
              external={linkExternal}
              to={`${urls.build.detections.rules.edit(rule.id)}${
                linkToRule?.toString
                  ? `?${stringifyUrlParams(linkToRule as Record<string, any>)}`
                  : ''
              }`}
              aria-label="Link to Rule"
              data-tid="link-to-detection"
            >
              <Heading
                variant="h6"
                wordBreak="break-word"
                flexShrink={1}
                display="flex"
                alignItems="center"
                mr={100}
              >
                {rule.displayName || rule.id}
              </Heading>
            </Link>
          ) : (
            <Heading
              variant="h6"
              wordBreak="break-word"
              aria-describedby="rule-description"
              flexShrink={1}
              display="flex"
              alignItems="center"
              mr={100}
            >
              {rule.displayName || rule.id}
            </Heading>
          )}
          {!omitBadges && (
            <Flex flexShrink={0} ml="auto" alignItems="center">
              <RelatedDestinationsV3
                destinations={detectionDestinations}
                loading={loadingDetectionDestinations}
                limit={5}
              />
              <BulletDividerV3 />
              <Flex gap={2} as="ul">
                <Box as="li">
                  <ManagedBadgeV3 managed={rule.managed} />
                </Box>
                <Box as="li">
                  <StatusBadgeV3 status="ENABLED" disabled={!rule.enabled} />
                </Box>
                <Box as="li">
                  <SeverityBadgeV3 severity={rule.severity} />
                </Box>
              </Flex>
            </Flex>
          )}
        </Flex>

        <Flex as="dl" fontSize={typographyTokens.fontSizes.sm} wrap="wrap" alignItems="center">
          <Box>
            <BulletedValueV3
              size="small"
              color={ruleTypeAccentColor}
              value={isScheduledRule ? 'Scheduled Rule' : 'Rule'}
              emphasized
            />
          </Box>
          <BulletDividerV3 />
          <Box>
            <Text as="dt" fontWeight="bold" display="inline" mr={1}>
              ID
            </Text>
            <Text as="dd" display="inline" color={tokens.content.text.neutral[3]}>
              {rule.id}
            </Text>
          </Box>
          {rule.parentId && (
            <>
              <BulletDividerV3 />
              <Flex>
                <Text as="dt" fontWeight="bold" mr={1}>
                  Parent ID
                </Text>
                <Box as="dd">
                  <Link as={RRLink} to={urls.build.detections.rules.details(rule.parentId)}>
                    {rule.parentId}
                  </Link>
                </Box>
              </Flex>
            </>
          )}
        </Flex>
        <Flex gap={1}>
          <LimitItemDisplayV3 limit={3} AdditionalItemsComponent={AdditionalBadgesJewelV3}>
            {isScheduledRule
              ? rule.scheduledQueries.map((item, index) => {
                  const hasValidTargetDestination =
                    rule.fullScheduledQueries && rule.fullScheduledQueries[index];

                  const scheduledRuleUrl = hasValidTargetDestination
                    ? `${urls.investigate.dataExplorer()}?savedQueryId=${
                        rule.fullScheduledQueries[index].id
                      }`
                    : urls.investigate.dataExplorer();

                  return (
                    <Link key={item} as={RRLink} to={scheduledRuleUrl}>
                      <Badge key={item} variant="neutral-outline">
                        {item}
                      </Badge>
                    </Link>
                  );
                })
              : rule.logTypes?.map(item => (
                  <Badge key={item} variant="neutral-outline">
                    {item}
                  </Badge>
                ))}
          </LimitItemDisplayV3>
        </Flex>
      </Card>
    </React.Fragment>
  );
};

export default React.memo(RuleDetailsBannerV3);
